import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Factory, Store as StoreIcon, Truck, Users, PlusCircle,
  FileText, ScanLine, PackageCheck, BadgeIndianRupee, ShieldCheck, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { network, purchaseOrders } from '../lib/api';
import PoSignaturePanel from '../components/PoSignaturePanel';

const ROLE_META = {
  factory:     { icon: Factory,   label: 'Factory / Brand-owner', childLabel: 'Distributor',  parent: null },
  distributor: { icon: Truck,     label: 'Distributor',           childLabel: 'Dealer',       parent: 'factory' },
  dealer:      { icon: Users,     label: 'Dealer',                childLabel: 'Retailer',     parent: 'distributor' },
  retailer:    { icon: StoreIcon, label: 'Retailer',              childLabel: 'Customers',    parent: 'dealer' },
};

const STATUS_META = {
  pending: { label: 'Awaiting payment', tone: 'bg-[#FFF8E1] text-[#7A4A00]' },
  payment_pending_verification: { label: 'Payment awaiting verification', tone: 'bg-[#FFF3E0] text-[#D35400]' },
  payment_rejected: { label: 'Payment rejected · resubmit', tone: 'bg-[#FFEBEE] text-[#C62828]' },
  confirmed: { label: 'Confirmed', tone: 'bg-[#E3F2FD] text-[#0D47A1]' },
  processing: { label: 'Processing', tone: 'bg-[#E3F2FD] text-[#0D47A1]' },
  dispatched: { label: 'Dispatched', tone: 'bg-[#E8F5E9] text-[#1B5E20]' },
  delivered: { label: 'Delivered', tone: 'bg-[#E8F5E9] text-[#1B5E20]' },
  cancelled: { label: 'Cancelled', tone: 'bg-[#F5F5F5] text-[#616161]' },
};

const TABS = [
  { id: 'entities', label: 'My MS-IDs', Icon: ShieldCheck },
  { id: 'catalog',  label: 'Catalog (Factory)', Icon: PackageCheck },
  { id: 'create',   label: 'Raise PO (Buyer)', Icon: PlusCircle },
  { id: 'mine',     label: 'My Purchase Orders', Icon: FileText },
];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function SupplyChain() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('entities');
  const [entities, setEntities] = useState([]);
  const [pos, setPos] = useState([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/'); return; }
    network.mine().then(r => setEntities(r.entities || []));
    purchaseOrders.mine().then(r => setPos(r.purchase_orders || []));
  }, [user, loading, nav, reload]);

  const myFactory = useMemo(() => entities.find(e => e.role === 'factory'), [entities]);
  const myBuyerEntity = useMemo(
    () => entities.find(e => e.role === 'distributor' || e.role === 'dealer'),
    [entities],
  );

  const refresh = () => setReload(v => v + 1);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-16 text-ink/60">Loading…</div>;
  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#D35400]">Supply Chain Hub</div>
          <h1 className="font-heading font-black text-3xl md:text-4xl mt-1">Factories, distributors &amp; POs</h1>
          <p className="text-sm text-ink/60 mt-1">
            Every business gets a globally-unique 10-character <b>MS-ID</b>. Buyers raise digital POs, upload payment proof, and factories verify — signed &amp; timestamped.
          </p>
        </div>
        <button data-testid="sc-refresh-btn" onClick={refresh}
          className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">
          <RefreshCw className="w-3 h-3"/>Refresh
        </button>
      </div>

      <div className="mt-6 flex gap-2 flex-wrap border-b border-border pb-1">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} data-testid={`sc-tab-${id}`} onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-sm font-semibold border-b-2 transition ${
              tab === id ? 'border-[#E67E22] text-[#D35400] bg-[#FFF8EE]' : 'border-transparent text-ink/60 hover:text-[#D35400]'
            }`}>
            <Icon className="w-4 h-4"/>{label}
          </button>
        ))}
      </div>

      {tab === 'entities' && <EntitiesTab entities={entities} onChanged={refresh}/>}
      {tab === 'catalog'  && <FactoryCatalogTab factory={myFactory} onChanged={refresh}/>}
      {tab === 'create'   && <CreatePoTab buyer={myBuyerEntity} onCreated={refresh}/>}
      {tab === 'mine'     && <MyPosTab pos={pos} entities={entities} onChanged={refresh}/>}
    </div>
  );
}

/* --------------------------- Tab: My MS-IDs --------------------------- */
function EntitiesTab({ entities, onChanged }) {
  const [form, setForm] = useState({
    role: 'retailer', business_name: '', phone: '',
    address: '', pincode: '', gstin: '', email: '', parent_ms_id: '',
  });
  const [busy, setBusy] = useState(false);
  const parentRole = ROLE_META[form.role]?.parent;

  const submit = async () => {
    if (!form.business_name || !form.phone || !form.address || !form.pincode) {
      toast.error('Business name, phone, address and pincode are all required.');
      return;
    }
    if (parentRole && !form.parent_ms_id) {
      toast.error(`A ${form.role} must link to its ${parentRole}’s MS-ID.`);
      return;
    }
    try {
      setBusy(true);
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const r = await network.register(payload);
      toast.success(`MS-ID assigned: ${r.entity.ms_id}`);
      setForm({ role: form.role, business_name: '', phone: '', address: '', pincode: '',
                gstin: '', email: '', parent_ms_id: '' });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not register entity');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-[1.1fr_1fr] gap-6">
      <section className="card-soft p-5">
        <div className="font-heading font-bold text-lg">Register a new entity</div>
        <p className="text-xs text-ink/60 mt-1">A dealer must link to a distributor; a distributor to a factory. Retailers link to a dealer.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
          <label className="text-xs font-semibold text-ink/70">
            Role
            <select data-testid="sc-entity-role" value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value, parent_ms_id: '' })}
              className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm">
              {Object.keys(ROLE_META).map(r => <option key={r} value={r}>{ROLE_META[r].label}</option>)}
            </select>
          </label>
          <input data-testid="sc-entity-business-name" placeholder="Business / brand name"
            value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="sc-entity-phone" placeholder="Phone (10 digits)"
            value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="sc-entity-pincode" placeholder="Pincode (6 digits)"
            value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="sc-entity-address" placeholder="Full address (incl. district / state)"
            value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
            className="md:col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="sc-entity-gstin" placeholder="GSTIN (optional)"
            value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="sc-entity-email" placeholder="Email (optional)"
            value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          {parentRole && (
            <div className="md:col-span-2 rounded-lg border border-[#E67E22]/30 bg-[#FFF8EE] p-3">
              <div className="text-[11px] uppercase tracking-wider font-bold text-[#D35400]">
                Link to your {parentRole}
              </div>
              <input data-testid="sc-entity-parent-ms-id"
                placeholder={`Parent ${parentRole} MS-ID (e.g. MS12345678)`}
                value={form.parent_ms_id}
                onChange={e => setForm({ ...form, parent_ms_id: e.target.value.toUpperCase() })}
                className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
            </div>
          )}
          <button data-testid="sc-entity-submit" disabled={busy} onClick={submit}
            className="btn-primary md:col-span-2">
            <PlusCircle className="w-4 h-4"/>{busy ? 'Registering…' : 'Register — get MS-ID'}
          </button>
        </div>
      </section>

      <section className="card-soft p-5">
        <div className="font-heading font-bold text-lg">My MS-IDs</div>
        {entities.length === 0 ? (
          <div className="mt-4 text-sm text-ink/60">No entities registered yet.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {entities.map(e => {
              const RoleIcon = ROLE_META[e.role]?.icon || StoreIcon;
              return (
                <div key={e.ms_id} data-testid={`sc-entity-row-${e.ms_id}`}
                  className="rounded-lg border border-border bg-white p-3 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#FFF8EE] grid place-items-center text-[#D35400]">
                    <RoleIcon className="w-5 h-5"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs font-bold tracking-wider text-[#1B5E20]">{e.ms_id}</div>
                    <div className="font-semibold text-sm truncate">{e.business_name}</div>
                    <div className="text-xs text-ink/60">{ROLE_META[e.role]?.label || e.role} · {e.pincode}</div>
                    {e.parent_ms_id && (
                      <div className="text-[11px] text-ink/50">↳ linked to <span className="font-mono">{e.parent_ms_id}</span></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

/* -------------------- Tab: Factory catalog -------------------- */
function FactoryCatalogTab({ factory, onChanged }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: '', sku: '', unit: 'pc', case_qty: '', price: '', moq: 1, description: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!factory) return;
    const r = await network.factoryCatalog(factory.ms_id);
    setRows(r.products || []);
  }, [factory]);
  useEffect(() => { load(); }, [load]);

  if (!factory) {
    return (
      <div className="mt-6 card-soft p-6 text-sm text-ink/70">
        Register your Factory / Brand-owner entity in the <b>My MS-IDs</b> tab first — then come back here to add products with MOQ.
      </div>
    );
  }

  const addProduct = async () => {
    if (!form.name || !form.price) { toast.error('Name and price required'); return; }
    try {
      setBusy(true);
      const payload = {
        ...form,
        price: parseFloat(form.price),
        moq: parseInt(form.moq) || 1,
        case_qty: form.case_qty ? parseInt(form.case_qty) : null,
      };
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === null) delete payload[k]; });
      await network.addProduct(payload);
      toast.success('Product added to factory catalog');
      setForm({ name: '', sku: '', unit: 'pc', case_qty: '', price: '', moq: 1, description: '' });
      load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add product');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 grid lg:grid-cols-[1fr_1fr] gap-6">
      <section className="card-soft p-5">
        <div className="font-heading font-bold text-lg">Add product to factory catalog</div>
        <div className="text-xs text-ink/60 mt-1">Factory <span className="font-mono">{factory.ms_id}</span> · {factory.business_name}</div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          <input data-testid="sc-prod-name" placeholder="Product name (e.g. Turmeric powder 500g)"
            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="md:col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="sc-prod-sku" placeholder="SKU (optional)"
            value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <select data-testid="sc-prod-unit" value={form.unit}
            onChange={e => setForm({ ...form, unit: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
            <option value="pc">per piece</option>
            <option value="case">per case</option>
            <option value="pallet">per pallet</option>
            <option value="kg">per kg</option>
            <option value="litre">per litre</option>
          </select>
          <input data-testid="sc-prod-case-qty" placeholder="Units per case"
            value={form.case_qty} onChange={e => setForm({ ...form, case_qty: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="sc-prod-price" placeholder="Wholesale price ₹"
            value={form.price} onChange={e => setForm({ ...form, price: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <input data-testid="sc-prod-moq" placeholder="MOQ (min. order qty)"
            value={form.moq} onChange={e => setForm({ ...form, moq: e.target.value })}
            className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <textarea data-testid="sc-prod-desc" placeholder="Description (specs / carton details / expiry policy)"
            value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="md:col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
          <button data-testid="sc-prod-submit" disabled={busy} onClick={addProduct}
            className="btn-primary md:col-span-2">
            <PlusCircle className="w-4 h-4"/>{busy ? 'Adding…' : 'Add to catalog'}
          </button>
        </div>
      </section>

      <section className="card-soft p-5">
        <div className="font-heading font-bold text-lg">Live catalog · {rows.length} SKUs</div>
        <div className="mt-3 space-y-2 max-h-[520px] overflow-auto pr-1">
          {rows.length === 0
            ? <div className="text-sm text-ink/60">Empty catalog. Add your first SKU on the left.</div>
            : rows.map(p => (
                <div key={p.id} data-testid={`sc-catalog-row-${p.id}`}
                  className="rounded-lg border border-border bg-white p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{p.name}</div>
                    <div className="text-xs text-ink/60">
                      {p.sku ? `${p.sku} · ` : ''}{money(p.price)} / {p.unit || 'pc'} · MOQ {p.moq}
                    </div>
                  </div>
                </div>
              ))
          }
        </div>
      </section>
    </div>
  );
}

/* -------------------- Tab: Create PO (buyer) -------------------- */
function CreatePoTab({ buyer, onCreated }) {
  const [factoryLookup, setFactoryLookup] = useState('');
  const [factory, setFactory] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [qtyByProd, setQtyByProd] = useState({});
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const doLookup = async () => {
    if (!factoryLookup) return;
    try {
      const r = await network.lookup(factoryLookup.toUpperCase());
      if (r.role !== 'factory') { toast.error(`MS-ID ${r.ms_id} is a ${r.role}, not a factory.`); return; }
      setFactory(r);
      const cat = await network.factoryCatalog(r.ms_id);
      setCatalog(cat.products || []);
      setQtyByProd({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'MS-ID not found');
    }
  };

  const items = useMemo(() =>
    catalog
      .filter(p => (qtyByProd[p.id] || 0) > 0)
      .map(p => ({ product_id: p.id, name: p.name, unit: p.unit, price: p.price, qty: qtyByProd[p.id] })),
    [catalog, qtyByProd],
  );
  const subtotal = items.reduce((s, l) => s + l.price * l.qty, 0);

  const submit = async () => {
    if (!buyer) { toast.error('Register your distributor/dealer MS-ID first.'); return; }
    if (!factory) { toast.error('Look up a factory MS-ID first.'); return; }
    if (!items.length) { toast.error('Add at least one item.'); return; }
    try {
      setBusy(true);
      const payload = {
        factory_ms_id: factory.ms_id,
        buyer_ms_id: buyer.ms_id,
        items: items.map(i => ({ product_id: i.product_id, qty: i.qty })),
        notes: notes || null,
      };
      const r = await purchaseOrders.create(payload);
      toast.success(`PO created: ${r.po.ord_id}`);
      setQtyByProd({}); setNotes('');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create PO');
    } finally {
      setBusy(false);
    }
  };

  if (!buyer) {
    return (
      <div className="mt-6 card-soft p-6 text-sm text-ink/70">
        Register your <b>Distributor</b> or <b>Dealer</b> MS-ID in the <b>My MS-IDs</b> tab first — only buyers can raise POs.
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-6">
      <section className="card-soft p-5">
        <div className="font-heading font-bold text-lg">Look up a factory</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <input data-testid="sc-po-factory-ms-id" placeholder="Factory MS-ID (e.g. MS12345678)"
            value={factoryLookup} onChange={e => setFactoryLookup(e.target.value.toUpperCase())}
            className="flex-1 rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
          <button data-testid="sc-po-factory-lookup-btn" onClick={doLookup}
            className="btn-primary"><Factory className="w-4 h-4"/>Look up</button>
        </div>
        {factory && (
          <div className="mt-3 rounded-lg border border-[#1B5E20]/20 bg-[#E8F5E9]/40 p-3">
            <div className="text-xs uppercase tracking-wider font-bold text-[#1B5E20]">Factory found</div>
            <div className="font-semibold">{factory.business_name} <span className="font-mono text-xs text-ink/60">· {factory.ms_id}</span></div>
            <div className="text-xs text-ink/60">Pincode {factory.pincode}</div>
          </div>
        )}
      </section>

      {factory && (
        <section className="card-soft p-5">
          <div className="font-heading font-bold text-lg">Add items to PO</div>
          {catalog.length === 0
            ? <div className="mt-2 text-sm text-ink/60">This factory has no active SKUs yet.</div>
            : (
              <div className="mt-3 space-y-2">
                {catalog.map(p => (
                  <div key={p.id} data-testid={`sc-po-item-${p.id}`}
                    className="flex items-center gap-3 rounded-lg border border-border bg-white p-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{p.name}</div>
                      <div className="text-xs text-ink/60">{money(p.price)} / {p.unit || 'pc'} · MOQ {p.moq}</div>
                    </div>
                    <input data-testid={`sc-po-qty-${p.id}`} type="number" min="0"
                      placeholder="Qty" value={qtyByProd[p.id] || ''}
                      onChange={e => setQtyByProd({ ...qtyByProd, [p.id]: parseInt(e.target.value) || 0 })}
                      className="w-24 rounded-lg border border-border bg-white px-2 py-1.5 text-sm text-right"/>
                  </div>
                ))}
              </div>
            )
          }
          <div className="mt-4 grid gap-2">
            <textarea data-testid="sc-po-notes" placeholder="Notes to factory (payment terms, transport…)"
              value={notes} onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
            <div className="flex items-center justify-between">
              <div className="text-sm">
                Subtotal: <b className="text-lg font-heading">{money(subtotal)}</b>
              </div>
              <button data-testid="sc-po-submit" disabled={busy || !items.length} onClick={submit}
                className="btn-primary">
                <FileText className="w-4 h-4"/>{busy ? 'Generating PDF…' : 'Create PO & generate PDF'}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/* -------------------- Tab: My purchase orders -------------------- */
function MyPosTab({ pos, entities, onChanged }) {
  const myMsIds = useMemo(() => new Set(entities.map(e => e.ms_id)), [entities]);
  const [openPo, setOpenPo] = useState(null);

  return (
    <div className="mt-6">
      {pos.length === 0
        ? <div className="card-soft p-6 text-sm text-ink/60">No POs yet. Raise one in the “Raise PO” tab.</div>
        : (
          <div className="grid gap-3">
            {pos.map(po => {
              const status = STATUS_META[po.status] || { label: po.status, tone: 'bg-[#F5F5F5] text-[#616161]' };
              const iAmFactory = myMsIds.has(po.factory_ms_id);
              const iAmBuyer = myMsIds.has(po.buyer_ms_id);
              return (
                <div key={po.id} data-testid={`sc-po-card-${po.ord_id}`}
                  className="card-soft p-4 flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[240px]">
                    <div className="text-xs uppercase tracking-wider font-bold text-[#D35400]">
                      {iAmFactory ? 'Incoming (as Factory)' : iAmBuyer ? 'Outgoing (as Buyer)' : 'PO'}
                    </div>
                    <div className="font-mono text-xs font-bold tracking-wider text-[#1B5E20]">{po.ord_id}</div>
                    <div className="font-semibold text-sm mt-1">
                      {po.buyer_name} → {po.factory_name}
                    </div>
                    <div className="text-xs text-ink/60">
                      {po.items?.length || 0} items · {money(po.subtotal)} · created {new Date(po.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${status.tone}`}>
                      {status.label}
                    </span>
                    <div className="flex flex-wrap gap-2 mt-2 justify-end">
                      {po.pdf_url && (
                        <a data-testid={`sc-po-pdf-${po.ord_id}`}
                          href={po.pdf_url} target="_blank" rel="noreferrer"
                          className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">
                          <FileText className="w-3 h-3"/>PDF
                        </a>
                      )}
                      <button data-testid={`sc-po-open-${po.ord_id}`} onClick={() => setOpenPo(po)}
                        className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">
                        Open
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      }

      {openPo && (
        <PoDetailDialog
          po={openPo}
          iAmFactory={myMsIds.has(openPo.factory_ms_id)}
          iAmBuyer={myMsIds.has(openPo.buyer_ms_id)}
          onClose={() => setOpenPo(null)}
          onChanged={() => { setOpenPo(null); onChanged(); }}
        />
      )}
    </div>
  );
}

/* -------------------- PO detail modal (with Proof of Payment) -------------------- */
function PoDetailDialog({ po, iAmFactory, iAmBuyer, onClose, onChanged }) {
  const [poFresh, setPoFresh] = useState(po);
  const [busy, setBusy] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [utr, setUtr] = useState('');
  const [amount, setAmount] = useState(po.subtotal || 0);
  const [mode, setMode] = useState('bank_transfer');
  const [payNote, setPayNote] = useState('');
  const [verifyNote, setVerifyNote] = useState('');

  const reload = useCallback(async () => {
    const r = await purchaseOrders.get(po.ord_id || po.id);
    setPoFresh(r);
  }, [po]);
  useEffect(() => { reload(); }, [reload]);

  const status = STATUS_META[poFresh.status] || { label: poFresh.status, tone: 'bg-[#F5F5F5] text-[#616161]' };
  const canSubmitPay = iAmBuyer && ['pending', 'payment_rejected'].includes(poFresh.status);
  const canVerify = iAmFactory && poFresh.status === 'payment_pending_verification';
  const canDispatch = iAmFactory && ['confirmed', 'processing'].includes(poFresh.status);
  const canDeliver = iAmBuyer && poFresh.status === 'dispatched';
  const latestPayment = (poFresh.payment_history || []).slice(-1)[0];

  const submitPayment = async () => {
    if (!receiptFile) { toast.error('Please attach a receipt / screenshot.'); return; }
    if (!utr || utr.length < 4) { toast.error('Enter your UTR / transaction reference.'); return; }
    if (!amount || amount <= 0) { toast.error('Enter the paid amount.'); return; }
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append('receipt', receiptFile);
      fd.append('utr_reference', utr.trim());
      fd.append('amount', String(amount));
      fd.append('payment_mode', mode);
      if (payNote) fd.append('note', payNote);
      const r = await purchaseOrders.submitPayment(poFresh.ord_id || poFresh.id, fd);
      toast.success(r.admin_alert
        ? 'Proof submitted · high-value payment — admin has been notified'
        : 'Proof submitted — factory will verify shortly');
      setReceiptFile(null); setUtr(''); setPayNote('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit payment');
    } finally {
      setBusy(false);
    }
  };

  const verifyPayment = async (approve) => {
    if (!approve && !verifyNote) { toast.error('Please give a reason when rejecting.'); return; }
    try {
      setBusy(true);
      await purchaseOrders.verifyPayment(poFresh.ord_id || poFresh.id, { approve, note: verifyNote || null });
      toast.success(approve ? 'Payment approved · PO confirmed' : 'Payment rejected — buyer will resubmit');
      setVerifyNote('');
      reload();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not verify payment');
    } finally {
      setBusy(false);
    }
  };

  const moveStatus = async (newStatus, note) => {
    try {
      setBusy(true);
      await purchaseOrders.updateStatus(poFresh.ord_id || poFresh.id, { status: newStatus, note: note || null });
      toast.success(`Moved to ${newStatus}`);
      reload();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update status');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="max-w-3xl w-full max-h-[92vh] overflow-auto bg-white rounded-2xl shadow-2xl p-6"
        onClick={e => e.stopPropagation()} data-testid={`sc-po-dialog-${poFresh.ord_id}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-[#D35400]">Purchase Order</div>
            <div className="font-mono text-sm font-bold text-[#1B5E20]">{poFresh.ord_id}</div>
            <div className="font-heading font-black text-xl mt-1">
              {poFresh.buyer_name} → {poFresh.factory_name}
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${status.tone}`}>
            {status.label}
          </span>
        </div>

        <div className="mt-4 grid gap-1 text-sm">
          {(poFresh.items || []).map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 truncate">{it.name} <span className="text-ink/50 text-xs">· {it.qty} {it.unit || 'pc'}</span></div>
              <div className="text-right font-mono">{money(it.line_total)}</div>
            </div>
          ))}
          <div className="flex justify-between font-heading font-bold mt-1 border-t border-border pt-2">
            <span>Subtotal</span><span>{money(poFresh.subtotal)}</span>
          </div>
        </div>

        <PoSignaturePanel po={poFresh} iAmFactory={iAmFactory} iAmBuyer={iAmBuyer} onSigned={reload}/>

        {/* Buyer — submit proof of payment */}
        {canSubmitPay && (
          <div className="mt-6 rounded-xl border border-[#E67E22]/25 bg-[#FFF8EE] p-4" data-testid="sc-pop-buyer-panel">
            <div className="font-heading font-bold text-base flex items-center gap-2 text-[#D35400]">
              <BadgeIndianRupee className="w-4 h-4"/> Confirm payment to factory
            </div>
            <p className="text-xs text-ink/60 mt-1">
              Once you’ve transferred, upload a screenshot / receipt and enter the UTR reference. Payments above ₹1,00,000 auto-alert the admin.
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <input data-testid="sc-pop-utr" placeholder="UTR / bank reference"
                value={utr} onChange={e => setUtr(e.target.value.toUpperCase())}
                className="rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
              <input data-testid="sc-pop-amount" type="number" placeholder="Amount ₹"
                value={amount} onChange={e => setAmount(parseFloat(e.target.value) || 0)}
                className="rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
              <select data-testid="sc-pop-mode" value={mode} onChange={e => setMode(e.target.value)}
                className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
                <option value="bank_transfer">Bank transfer</option>
                <option value="neft">NEFT</option>
                <option value="rtgs">RTGS</option>
                <option value="imps">IMPS</option>
                <option value="upi">UPI</option>
              </select>
              <label className="rounded-lg border border-dashed border-border bg-white px-3 py-2 text-sm cursor-pointer text-ink/70">
                {receiptFile ? receiptFile.name : 'Receipt (JPG / PNG / PDF) — max 6 MB'}
                <input data-testid="sc-pop-file" type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                  onChange={e => setReceiptFile(e.target.files?.[0] || null)}
                  className="hidden"/>
              </label>
              <textarea data-testid="sc-pop-note" placeholder="Note (optional)"
                value={payNote} onChange={e => setPayNote(e.target.value)} rows={2}
                className="md:col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
              <button data-testid="sc-pop-submit-btn" disabled={busy} onClick={submitPayment}
                className="btn-primary md:col-span-2">
                <ShieldCheck className="w-4 h-4"/>{busy ? 'Uploading…' : 'Submit proof of payment'}
              </button>
            </div>
          </div>
        )}

        {/* Factory — verify payment */}
        {canVerify && latestPayment && (
          <div className="mt-6 rounded-xl border border-[#1B5E20]/25 bg-[#E8F5E9]/50 p-4" data-testid="sc-pop-factory-panel">
            <div className="font-heading font-bold text-base flex items-center gap-2 text-[#1B5E20]">
              <ShieldCheck className="w-4 h-4"/> Verify buyer’s payment
            </div>
            <div className="mt-2 text-sm">
              <div><b>UTR:</b> <span className="font-mono">{latestPayment.utr_reference}</span></div>
              <div><b>Amount:</b> {money(latestPayment.amount)} · <b>Mode:</b> {String(latestPayment.payment_mode || '').toUpperCase()}</div>
              {latestPayment.note && <div className="text-ink/70"><b>Note:</b> {latestPayment.note}</div>}
              {latestPayment.receipt_url && (
                <a data-testid="sc-pop-receipt-view"
                  href={latestPayment.receipt_url} target="_blank" rel="noreferrer"
                  className="inline-block mt-2 pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">
                  <FileText className="w-3 h-3"/> View receipt
                </a>
              )}
            </div>
            <textarea data-testid="sc-pop-verify-note" placeholder="Verify note / rejection reason"
              value={verifyNote} onChange={e => setVerifyNote(e.target.value)} rows={2}
              className="mt-3 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
            <div className="mt-2 flex flex-wrap gap-2">
              <button data-testid="sc-pop-approve-btn" disabled={busy}
                onClick={() => verifyPayment(true)} className="btn-primary">
                Approve · confirm PO
              </button>
              <button data-testid="sc-pop-reject-btn" disabled={busy}
                onClick={() => verifyPayment(false)}
                className="pill !bg-white text-[#C62828] border-[#FFCDD2] hover:border-[#C62828]">
                Reject
              </button>
            </div>
          </div>
        )}

        {/* Factory move actions */}
        {canDispatch && (
          <div className="mt-6 flex flex-wrap gap-2">
            <button data-testid="sc-po-processing-btn" onClick={() => moveStatus('processing')} disabled={busy}
              className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">Mark processing</button>
            <button data-testid="sc-po-dispatch-btn" onClick={() => moveStatus('dispatched')} disabled={busy}
              className="btn-primary">Dispatch</button>
          </div>
        )}
        {canDeliver && (
          <div className="mt-6 flex flex-wrap gap-2">
            <button data-testid="sc-po-scan-receive-btn" disabled={busy}
              onClick={async () => {
                try {
                  setBusy(true);
                  await purchaseOrders.scanReceive(poFresh.ord_id || poFresh.id);
                  toast.success('Received via scan');
                  reload(); onChanged();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Could not scan-receive');
                } finally { setBusy(false); }
              }}
              className="btn-primary"><ScanLine className="w-4 h-4"/>Scan to receive</button>
          </div>
        )}

        {/* Timeline */}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider font-bold text-ink/50">Timeline</div>
          <div className="mt-2 space-y-1 text-sm">
            {(poFresh.timeline || []).map((t, i) => (
              <div key={i} className="flex justify-between gap-2 border-b border-border/60 py-1">
                <div>
                  <b className="capitalize">{String(t.status).replace(/_/g, ' ')}</b>
                  <span className="text-ink/60"> · {t.by_role} {t.by_ms_id}</span>
                  {t.note && <div className="text-xs text-ink/60">{t.note}</div>}
                </div>
                <div className="text-xs text-ink/50">{new Date(t.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 text-right">
          <button data-testid="sc-po-close-btn" onClick={onClose}
            className="pill !bg-white hover:border-[#E67E22] hover:text-[#D35400]">Close</button>
        </div>
      </div>
    </div>
  );
}
